import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaTrash, FaSave, FaTimes, FaPlus } from "react-icons/fa";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";

export default function DepartmentCRUD() {
  const [departments, setDepartments] = useState([]);
  const [newDepartment, setNewDepartment] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const navigate = useNavigate();

  const fetchDepartments = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/departments/all`);
      const data = await res.json();
      setDepartments(data);
    } catch (error) {
      toast.error("Failed to load departments");
    }
  };

  useEffect(() => {
    fetchDepartments();
  }, []);

  useEffect(() => {
  const token = sessionStorage.getItem("token");
  const roles = JSON.parse(sessionStorage.getItem("roles") || "[]");

  if (!token) {
    toast.error("You must be logged in to access this page.");
    navigate("/");
    return;
  }

  // ✅ check admin role
  if (!roles.includes("ROLE_ADMIN")) {
    toast.error("You are not authorized to access this page.");
    navigate("/");
    return;
  }
}, [navigate]);


  const handleAdd = async () => {
    if (!newDepartment.trim()) {
      toast.warning("Please enter a department name.");
      return;
    }
    await fetch(`${API_BASE_URL}/api/departments/add`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: newDepartment }),
    });
    toast.success("Department added successfully!");
    setNewDepartment("");
    fetchDepartments();
  };

  const handleUpdate = async (id) => {
    if (!editName.trim()) {
      toast.warning("Please enter a valid name.");
      return;
    }
    await fetch(`${API_BASE_URL}/api/departments/update/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: editName }),
    });
    toast.success("Department updated!");
    setEditingId(null);
    fetchDepartments();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this department?")) return;
    await fetch(`${API_BASE_URL}/api/departments/delete/${id}`, {
      method: "DELETE",
    });
    toast.info("Department deleted.");
    fetchDepartments();
  };

  return (
    <>
      {/* ✅ Fixed Navbar */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          zIndex: 1000,
        }}
      >
        <AdminNavbar />
      </div>

      {/* ✅ Page Content */}
      <div
        className="d-flex flex-column p-4"
        style={{
          minHeight: "100vh",
          backgroundColor: "#F5F7FB",
          marginTop: "110px", // 👈 Pushes content below fixed navbar
        }}
      >
        <h3 className="fw-bold text-dark mb-4 text-start">
          Department Management
        </h3>

        <div className="row flex-grow-1 g-4">
          {/* Left column - Compact Add form */}
          <div className="col-md-4 d-flex">
            <div
              className="card shadow-sm border-0 flex-fill"
              style={{
                borderRadius: "12px",
                height: "fit-content",
                alignSelf: "flex-start",
              }}
            >
              <div
                className="card-header fw-semibold"
                style={{
                  backgroundColor: "#DFE4EB",
                  color: "black",
                  borderTopLeftRadius: "12px",
                  borderTopRightRadius: "12px",
                }}
              >
                Add New Department
              </div>
              <div className="card-body">
                <input
                  type="text"
                  className="form-control mb-3"
                  placeholder="Enter department name"
                  value={newDepartment}
                  onChange={(e) => setNewDepartment(e.target.value)}
                />
                <button
                  className="btn text-dark w-40"
                  style={{ backgroundColor: "#DFE4EB" }}
                  onClick={handleAdd}
                >
                  <FaPlus className="me-2" />
                  Add Department
                </button>
              </div>
            </div>
          </div>

          {/* Right column - Department List */}
          <div className="col-md-8 d-flex">
            <div
              className="card shadow-sm border-0 flex-fill"
              style={{ borderRadius: "12px" }}
            >
              <div
                className="card-header fw-semibold d-flex justify-content-between align-items-center"
                style={{
                  backgroundColor: "#DFE4EB",
                  borderTopLeftRadius: "12px",
                  borderTopRightRadius: "12px",
                }}
              >
                <span>All Departments</span>
                <span className="badge bg-secondary">
                  {departments.length} Total
                </span>
              </div>

              <div
                className="card-body p-0"
                style={{
                  maxHeight: "70vh",
                  overflowY: "auto",
                }}
              >
                <ul className="list-group list-group-flush">
                  {departments.length === 0 && (
                    <li className="list-group-item text-muted text-center py-3">
                      No departments found.
                    </li>
                  )}
                  {departments.map((d) => (
                    <li
                      key={d.id}
                      className="list-group-item d-flex justify-content-between align-items-center"
                    >
                      {editingId === d.id ? (
                        <input
                          type="text"
                          className="form-control me-2"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          style={{ maxWidth: "300px" }}
                        />
                      ) : (
                        <span className="fw-medium">{d.name}</span>
                      )}

                      <div>
                        {editingId === d.id ? (
                          <>
                            <button
                              className="btn btn-success btn-sm me-2"
                              onClick={() => handleUpdate(d.id)}
                            >
                              <FaSave /> Save
                            </button>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => setEditingId(null)}
                            >
                              <FaTimes /> Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              className="btn  btn-sm me-2"
                              onClick={() => {
                                setEditingId(d.id);
                                setEditName(d.name);
                              }}
                            >
                              <FaEdit /> 
                            </button>
                            <button
                              className="btn  btn-sm"
                              onClick={() => handleDelete(d.id)}
                            >
                              <FaTrash /> 
                            </button>
                          </>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
